/**
 * The router: code-based, one route, every piece of state in the search
 * (D01). No `@tanstack/router-plugin`, no file routes.
 *
 * The router's scroll restoration is off: the page never scrolls —
 * `#app-frame` is exactly the viewport and screens scroll inside inner
 * regions (index.css) — and the screen stack keeps every visited screen
 * mounted, restoring its own offsets (`screen-stack.tsx`, `D193`).
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { setThemeUrlWriter } from "../stores/prefs";
import {
  RecoverScreen,
  reportBoundaryError,
} from "./error-reporting/error-boundary";
import { mergeSearch } from "./layers";
import {
  coachRedirect,
  isCoachScreen,
  parseAppSearch,
  type Search,
  searchSchema,
  stringifyAppSearch,
} from "./search";
import { Shell } from "./shell";

const rootRoute = createRootRoute({ component: Outlet });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: searchSchema,
  // `screen=coach` (the prototype's coach screen) is the coach overlay over
  // Today, and the URL is rewritten with REPLACE (A2 index.tsx:50-54).
  beforeLoad: ({ search }) => {
    if (isCoachScreen(search)) {
      throw redirect({ to: "/", search: coachRedirect(search), replace: true });
    }
  },
  component: Shell,
});

const routeTree = rootRoute.addChildren([indexRoute]);

export const router = createRouter({
  routeTree,
  // No view transitions (`D190`). Every push — a tab, a sheet, an overlay —
  // ran as a full-page `document.startViewTransition`: a frame to capture the
  // old view, rendering paused through the load, then the browser's default
  // 250 ms cross-fade, during which the page takes no taps. The screens fade
  // on their own (`features/shell/layout.tsx`, A2's motion) and sheets slide;
  // the root cross-fade was a third animation over both, and most of what
  // made a tap feel late.
  defaultViewTransition: false,
  // Off (`D193`): every visited screen stays mounted (`screen-stack.tsx`)
  // and puts its own scroll offsets back when it is shown again. A tab
  // switch is a REPLACE — a new history key with nothing cached — so the
  // router's restoration would only fight it.
  scrollRestoration: false,
  parseSearch: parseAppSearch,
  // Unknown params (the prototype's `state=`) are dropped from the URL on
  // the next navigation, not only from the validated search.
  search: { strict: true },
  stringifySearch: stringifyAppSearch,
  // Every route match has its own error boundary, which would otherwise catch
  // a render error before the root one (main.tsx) sees it, show TanStack's
  // developer panel and report nothing. T69: report it, and recover in A2.
  defaultErrorComponent: RecoverScreen,
  defaultOnCatch: reportBoundaryError,
});

// `?theme=` is read once, at boot, by the prefs store — which is created
// before this router — and is stale from then on. Dropping it has to go
// through the router: `@tanstack/history` caches `currentLocation` and
// updates it only on its own push/replace and on `popstate`, so the raw
// `history.replaceState` the store falls back to leaves `theme` in the
// router's search, the next `navigate()` writes it back into the URL, and the
// reload after that overrides the theme the person picked. Same screen, same
// layer state, so REPLACE.
setThemeUrlWriter(() => {
  router.navigate({
    to: "/",
    replace: true,
    search: (prev: Search) => mergeSearch(prev, { theme: undefined }),
    state: (prev) => prev,
  });
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
